import type { WixConfig } from '@wix/cli-app-definitions';
import {
  loadDashboardMenuPluginComponents,
  loadDashboardPluginComponents,
  loadDashboardModalComponents,
  loadDashboardPageComponents,
  loadDashboardSidebarCategoryComponent,
  loadMonitoringComponents,
  createTopologyComponent,
} from './dashboard/components.js';
import {
  loadEmbeddedScriptComponents,
  loadCustomElementComponents,
  loadSitePluginComponents,
  loadBlocksComponents,
  loadSiteComponents,
} from './site/components.js';
import type { DevCenterComponent } from './dev-center-component.js';
import { loadWixFrontendDependencies } from './site/frontend-dependencies.js';
import {
  loadBackendApis,
  loadBackendEvents,
  loadBackendWebMethods,
  loadServicePlugins,
} from './backend/components.js';

export async function loadDevCenterComponents({
  projectFolder,
  config,
}: {
  projectFolder: string;
  config: WixConfig;
}): Promise<DevCenterComponent[]> {
  const wixFrontendDependencies =
    await loadWixFrontendDependencies(projectFolder);

  const [
    dashboardPages,
    dashboardPlugins,
    dashboardMenuPlugins,
    dashboardModals,
    embeddedScripts,
    customElements,
    sitePlugins,
    blocksComponents,
    apis,
    webMethod,
    servicePlugins,
    events,
    monitoringComponent,
    siteComponents,
  ] = await Promise.all([
    loadDashboardPageComponents(projectFolder),
    loadDashboardPluginComponents(projectFolder),
    loadDashboardMenuPluginComponents(projectFolder),
    loadDashboardModalComponents(projectFolder),
    loadEmbeddedScriptComponents(projectFolder, wixFrontendDependencies),
    loadCustomElementComponents(projectFolder, wixFrontendDependencies),
    loadSitePluginComponents(projectFolder, wixFrontendDependencies),
    loadBlocksComponents(projectFolder, config),
    loadBackendApis(projectFolder),
    loadBackendWebMethods(projectFolder),
    loadServicePlugins(projectFolder),
    loadBackendEvents(projectFolder),
    loadMonitoringComponents(projectFolder, config),
    loadSiteComponents(projectFolder),
  ]);

  const dashboardSidebarCategories =
    loadDashboardSidebarCategoryComponent(config);

  const hasBackendComponents =
    apis.length + webMethod.length + servicePlugins.length + events.length > 0;

  const topologyComponent = hasBackendComponents
    ? [await createTopologyComponent(projectFolder)]
    : [];

  return [
    ...dashboardPages,
    ...dashboardPlugins,
    ...dashboardMenuPlugins,
    ...dashboardModals,
    ...embeddedScripts,
    ...dashboardSidebarCategories,
    ...customElements,
    ...apis,
    ...webMethod,
    ...servicePlugins,
    ...events,
    ...topologyComponent,
    ...sitePlugins,
    ...monitoringComponent,
    ...blocksComponents,
    ...siteComponents,
  ];
}
